import { styled } from 'styled-components';
import cs from 'classnames';
import Media from '@dvd/components/Media';
import SocialProof from '@dvd/modules/SocialProof';
import * as Styled from '@lib/styles';
import { ReactNode } from 'react';

// Per design we don't want any bottom margin on the messaging
export const HeroMessaging = styled.div`
  text-align: center;

  & > p {
    margin-block-end: 0;
  }
`;

export const HeroMedia = styled(Media)`
  margin-block-start: ${({ theme }) => theme.spacing[3]};
  margin-block-end: 0;

  ${({ theme }) => theme.mq.minWidth.large} {
    margin-block-start: ${({ theme }) => theme.spacing[4]};
  }
`;

export const HeroLogos = styled(SocialProof)`
  padding-block-end: 0;
  margin-block-start: ${({ theme }) => theme.spacing[1]};

  ${({ theme }) => theme.mq.minWidth.large} {
    margin-block-start: ${({ theme }) => theme.spacing[4]};
  }
`;

export const HeroSubtitle = styled.p`
  font-family: var(--font-walsheim);
  font-size: ${({ theme }) => theme.typography.fontSize.headingOne};
  font-weight: ${({ theme }) => theme.typography.fontWeight.headingOne};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.headingOne};
  line-height: ${({ theme }) => theme.typography.lineHeight.headingOne};
  text-wrap: balance;

  /* 12px (mobile) / 16px (desktop) between the headline and the description.
     Eyebrow -> headline spacing is owned by Styled.Eyebrow. */
  margin-block-end: ${({ theme }) => theme.spacing[0.75]};

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${({ theme }) => theme.typography.fontSize.desktop.headingOne};
    margin-block-end: ${({ theme }) => theme.spacing[1]};
  }
`;

const HeroDescriptionWrapper = styled(Styled.BodyLg)`
  text-wrap: balance;

  &.bodyMd {
    font-size: ${({ theme }) => theme.typography.fontSize.bodyMd};

    ${({ theme }) => theme.mq.minWidth.large} {
      font-size: ${({ theme }) => theme.typography.fontSize.desktop.bodyMd};
    }
  }

  &.longDescription {
    /* Remove bottom margin from last element when HTML is used for description. */
    :last-child {
      margin-block-end: 0;
    }
  }
`;

export const HeroDescription = ({
  description,
  size = 'bodyLg',
}: {
  description: ReactNode | string;
  size?: 'bodyLg' | 'bodyMd';
}) => {
  const descriptionIsString = typeof description === 'string';

  return (
    <HeroDescriptionWrapper
      as={descriptionIsString ? 'p' : 'div'}
      className={cs(size, { longDescription: !descriptionIsString })}
    >
      {description}
    </HeroDescriptionWrapper>
  );
};

export const HeroComputedMedia = ({
  hashedId,
  imageSrc,
  imageAlt,
  imageWidth = undefined,
  imageHeight = undefined,
  variant,
  aspect,
}) => {
  const { computedHeight, computedWidth } =
    variant === 'portrait'
      ? { computedHeight: 528, computedWidth: 704 }
      : { computedHeight: 613, computedWidth: 1088 };
  if (hashedId) {
    return <HeroMedia hashedId={hashedId} aspect={aspect} />;
  }
  if (imageSrc) {
    return (
      <HeroMedia
        alt={imageAlt}
        height={imageHeight ?? computedHeight}
        src={imageSrc}
        width={imageWidth ?? computedWidth}
      />
    );
  }
  return null;
};
